/**
 * Client-side group-by + aggregation over a resolved table. Operates on the full
 * fetched row set (the explorer fetches all matching rows when grouping is on, so
 * groups span the whole result, not just a page). Pure → unit-tested.
 */
import { cellText } from './cell-text'
import type { ResolvedColumn, ResolvedRow } from './spec-resolver'

export interface GroupAggregate {
  /** colId → numeric sum (only for numeric/calc columns with parseable values). */
  sums: Record<string, number>
  /** colId → numeric average. */
  avgs: Record<string, number>
}

export interface RowGroup {
  key: string
  label: string
  rows: ResolvedRow[]
  count: number
  agg: GroupAggregate
}

export interface GroupedResult {
  groups: RowGroup[]
  /** grand totals across all rows. */
  total: GroupAggregate
  totalCount: number
}

function isNumericColumn(c: ResolvedColumn): boolean {
  return c.kind === 'calc' || c.fieldType === 'number'
}

function aggregate(rows: ResolvedRow[], numericCols: ResolvedColumn[]): GroupAggregate {
  const sums: Record<string, number> = {}
  const counts: Record<string, number> = {}
  for (const col of numericCols) {
    let s = 0, n = 0
    for (const r of rows) {
      const v = r.cells[col.id]
      const num = typeof v === 'number' ? v : Number(v)
      if (Number.isFinite(num)) { s += num; n++ }
    }
    sums[col.id] = Math.round(s * 100) / 100
    counts[col.id] = n
  }
  const avgs: Record<string, number> = {}
  for (const col of numericCols) {
    avgs[col.id] = counts[col.id] ? Math.round((sums[col.id] / counts[col.id]) * 100) / 100 : 0
  }
  return { sums, avgs }
}

export function groupRows(
  columns: ResolvedColumn[],
  rows: ResolvedRow[],
  groupColId: string,
): GroupedResult {
  const groupCol = columns.find((c) => c.id === groupColId)
  const numericCols = columns.filter(isNumericColumn)

  const map = new Map<string, ResolvedRow[]>()
  for (const r of rows) {
    const label = groupCol ? cellText(groupCol, r) : '—'
    if (!map.has(label)) map.set(label, [])
    map.get(label)!.push(r)
  }

  const groups: RowGroup[] = [...map.entries()]
    .map(([label, gr]) => ({ key: label, label, rows: gr, count: gr.length, agg: aggregate(gr, numericCols) }))
    .sort((a, b) => b.count - a.count)

  return { groups, total: aggregate(rows, numericCols), totalCount: rows.length }
}
